import { Injectable } from '@angular/core';
import { HttpContext, HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { defer, Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { AppInsightsService } from '../app-insights.service';

export interface UiAction { id: string; name: string; }
export const UI_ACTION = new HttpContextToken<UiAction | undefined>(() => undefined);

// Pass this context through generated-client options to group a multi-request action.
export function createActionContext(name: string): HttpContext {
  return new HttpContext().set(UI_ACTION, { id: crypto.randomUUID().replace(/-/g, ''), name });
}

@Injectable()
export class TelemetryInterceptor implements HttpInterceptor {
  constructor(private readonly config: AppConfigService, private readonly insights: AppInsightsService) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const base = new URL(this.config.config?.apiConfig?.basePath || '/', window.location.origin);
    const url = new URL(request.url, window.location.origin);
    const prefix = base.pathname.replace(/\/$/, '');
    if (url.origin !== base.origin || !(url.pathname === prefix || url.pathname.startsWith(`${prefix}/`))) {
      return next.handle(request);
    }

    // Capture once per intercepted request; retries retain the action while parallel
    // requests keep independent contexts. HttpClient creates this chain per subscription.
    const action = request.context.get(UI_ACTION) || {
      id: crypto.randomUUID().replace(/-/g, ''), name: `${request.method} ${url.pathname}`
    };
    return defer(() => {
      let headers = request.headers.set('X-UI-Action-Id', action.id);
      const sessionId = this.insights.getSessionId();
      if (sessionId) headers = headers.set('X-UI-Session-Id', sessionId);
      const started = performance.now();
      let status: number | undefined;
      let traceId: string | null = null;
      return new Observable<HttpEvent<unknown>>(subscriber => this.insights.withAction(action.id, action.name, () =>
        next.handle(request.clone({ headers })).pipe(
          tap({
            next: response => {
              if (response instanceof HttpResponse) {
                status = response.status;
                traceId = response.headers.get('X-Trace-Id');
              }
            },
            error: (error: unknown) => {
              if (error instanceof HttpErrorResponse) {
                status = error.status;
                traceId = error.headers.get('X-Trace-Id');
              }
            }
          }),
          // One summary per request attempt; outcome describes HTTP, not business success.
          finalize(() => {
            let outcome = 'failed';
            if (status === undefined) {
              outcome = 'cancelled';
            } else if (status >= 200 && status < 400) {
              outcome = 'success';
            }
            this.insights.logAction('GatewayActionFinished', {
              ActionId: action.id, ActionName: action.name, ServerTraceId: traceId,
              status, outcome,
              durationMs: Math.round(performance.now() - started)
            });
          })
        ).subscribe(subscriber)
      ));
    });
  }
}
